import { useState } from 'react';
import { useLiveApp } from '../context/LiveAppContext';
import { Metric } from '../types/scorecard';

export function HierarchicalScorecard() {
 const { scorecard, authState, updateMetric, addMetric } = useLiveApp();
 const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
 const [selectedLevel1, setSelectedLevel1] = useState<string | null>(null);
 const [selectedLevel2, setSelectedLevel2] = useState<string | null>(null);
 const [selectedLevel3, setSelectedLevel3] = useState<string | null>(null);
 const [editingMetric, setEditingMetric] = useState<string | null>(null);
 const [editValue, setEditValue] = useState<string>('');
 const [showInactiveMetrics, setShowInactiveMetrics] = useState<boolean>(true);
 const [isAddingCard, setIsAddingCard] = useState<{ level: number, parentId: string } | null>(null);
 const [newCardName, setNewCardName] = useState<string>('');
 const [newCardDescription, setNewCardDescription] = useState<string>('');
 const [newCardOwner, setNewCardOwner] = useState<string>('');
 const [newCardIsActive, setNewCardIsActive] = useState<boolean>(true);

 const isAdmin = authState.user?.role === 'admin';

 const selectedCategoryData = selectedCategory
  ? scorecard.currentVersion.find(c => c.id === selectedCategory)
  : null;

 const selectedLevel1Data = selectedCategoryData && selectedLevel1
  ? selectedCategoryData.levels[0]?.metrics.find(m => m.id === selectedLevel1)
  : null;

 // Debug logging
 if (selectedLevel1Data) {
  console.log('Selected Level 1 Data:', selectedLevel1Data);
  console.log('SubMetrics:', selectedLevel1Data.subMetrics);
 }

 const selectedLevel2Data = selectedLevel1Data && selectedLevel2
  ? selectedLevel1Data.subMetrics?.find(m => m.id === selectedLevel2)
  : null;

 const selectedLevel3Data = selectedLevel2Data && selectedLevel3
  ? selectedLevel2Data.subMetrics?.find(m => m.id === selectedLevel3)
  : null;

 // Filter metrics based on active status
 const filterMetrics = (metrics: Metric[]): Metric[] => {
  return metrics.filter(metric => showInactiveMetrics || metric.isActive !== false);
 };

 const handleEditMetric = (metric: Metric) => {
  setEditingMetric(metric.id);
  setEditValue(metric.value);
 };

 const handleSaveMetric = (categoryId: string, level: number, metricId: string) => {
  updateMetric(categoryId, level, metricId, { value: editValue });
  setEditingMetric(null);
  setEditValue('');
 };

 const handleCancelEdit = () => {
  setEditingMetric(null);
  setEditValue('');
 };

 const handleToggleActive = async (categoryId: string, metricId: string, isActive: boolean) => {
  await updateMetric(categoryId, 1, metricId, { isActive });
 };

 const handleAddCard = (level: number, parentId: string) => {
  setIsAddingCard({ level, parentId });
  setNewCardName('');
  setNewCardDescription('');
  setNewCardOwner('');
  setNewCardIsActive(true);
 };

 const handleSaveNewCard = async () => {
  if (!isAddingCard || !newCardName.trim()) return;
  
  const newMetric = {
   name: newCardName.trim(),
   description: newCardDescription.trim(),
   value: '',
   target: '',
   owner: newCardOwner.trim(),
   isActive: newCardIsActive,
   subMetrics: []
  };

  await addMetric(isAddingCard.parentId, isAddingCard.level, newMetric);
  setIsAddingCard(null);
  setNewCardName('');
  setNewCardDescription('');
  setNewCardOwner('');
  setNewCardIsActive(true);
 };

 const handleCancelAddCard = () => {
  setIsAddingCard(null);
  setNewCardName('');
  setNewCardDescription('');
  setNewCardOwner('');
  setNewCardIsActive(true);
 };


 const renderMetricCard = (metric: Metric, level: number, categoryId: string, isClickable: boolean = true) => {
  const isInactive = metric.isActive === false;
  
  return (
   <div
    key={metric.id}
    className={`p-4 rounded-lg border transition-all duration-200 ${
     isInactive 
      ? 'bg-gray-50 border-gray-300 opacity-60' 
      : isClickable 
       ? 'cursor-pointer hover:bg-blue-50 hover:border-blue-200 border-gray-200 bg-white shadow-sm hover:shadow-md' 
       : 'border-gray-200 bg-white shadow-sm'
    }`}
    onClick={isClickable && !isInactive ? () => {
     if (level === 1) setSelectedLevel1(metric.id);
     else if (level === 2) setSelectedLevel2(metric.id);
     else if (level === 3) setSelectedLevel3(metric.id);
    } : undefined}
   >
    <div className="space-y-3">
     <div className="flex justify-between items-start">
      <div className="flex-1 min-w-0">
       <div className={`font-semibold text-sm flex items-center gap-2 ${isInactive ? 'text-gray-500' : 'text-gray-900'}`}>
        {metric.name}
        {isInactive && <span className="text-xs bg-gray-200 px-2 py-0.5 rounded text-gray-600 flex-shrink-0">Inactive</span>}
       </div>
       {metric.owner && (
        <div className={`text-xs font-medium mt-1 ${isInactive ? 'text-gray-400' : 'text-blue-600'}`}>
          {metric.owner}
        </div>
       )}
       <div className={`text-xs mt-2 ${isInactive ? 'text-gray-500' : 'text-gray-600'}`}>{metric.description}</div>
      </div>
      {isClickable && (
       <div className="text-gray-400 text-lg ml-3 flex-shrink-0">
        {level < 4 ? '▶' : ''}
       </div>
      )}
     </div>

     {/* Admin controls row */}
     {isAdmin && (
      <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-gray-100" onClick={e => e.stopPropagation()}>
       <button
        onClick={() => handleToggleActive(categoryId, metric.id, !metric.isActive)}
        className={`text-xs px-3 py-1 rounded-full font-medium transition-colors ${
         metric.isActive 
          ? 'bg-green-100 text-green-700 hover:bg-green-200' 
          : 'bg-red-100 text-red-700 hover:bg-red-200'
        }`}
       >
        {metric.isActive ? 'Active' : 'Inactive'}
       </button>
       
       {editingMetric === metric.id ? (
        <div className="flex-1 min-w-0">
         <input
          type="text"
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          className="w-full text-xs p-2 border rounded-md"
          autoFocus
          placeholder="Enter metric value"
          onKeyDown={(e) => {
           if (e.key === 'Enter') {
            handleSaveMetric(categoryId, level, metric.id);
           }
           if (e.key === 'Escape') {
            handleCancelEdit();
           }
          }}
         />
         <div className="flex gap-2 mt-2">
          <button
           onClick={() => handleSaveMetric(categoryId, level, metric.id)}
           className="text-xs bg-blue-500 text-white px-3 py-1 rounded-md hover:bg-blue-600 transition-colors"
          >
           Save
          </button>
          <button
           onClick={handleCancelEdit}
           className="text-xs bg-gray-500 text-white px-3 py-1 rounded-md hover:bg-gray-600 transition-colors"
          >
           Cancel
          </button>
         </div>
        </div>
       ) : (
        <button
         onClick={() => handleEditMetric(metric)}
         className="text-xs text-blue-600 hover:text-blue-800 cursor-pointer px-2 py-1 rounded hover:bg-blue-50 transition-colors"
        >
         {metric.value || '[empty]'} (edit)
        </button>
       )}
      </div>
     )}

     {/* Non-admin value display */}
     {!isAdmin && metric.value && (
      <div className="text-xs text-gray-600 pt-2 border-t border-gray-100">
       Value: {metric.value}
      </div>
     )}
    </div>
   </div>
  );
 };

 const renderAddCardForm = (level: number, parentId: string) => {
  if (!isAddingCard || isAddingCard.level !== level || isAddingCard.parentId !== parentId) {
   return (
    <button
     onClick={() => handleAddCard(level, parentId)}
     className="w-full p-4 border-2 border-dashed border-gray-300 rounded-lg text-gray-500 hover:border-blue-400 hover:text-blue-600 transition-colors duration-200 flex items-center justify-center gap-2"
    >
     <span className="text-xl">+</span>
     <span className="text-sm font-medium">Add New Card</span>
    </button>
   );
  }

  return (
   <div className="p-4 border-2 border-blue-300 rounded-lg bg-blue-50 space-y-3">
    <h4 className="text-sm font-semibold text-gray-900">Add New Metric</h4>
    <div className="space-y-2">
     <input
      type="text"
      value={newCardName}
      onChange={(e) => setNewCardName(e.target.value)}
      placeholder="Metric name"
      className="w-full text-sm p-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
      autoFocus
     />
     <input
      type="text"
      value={newCardOwner}
      onChange={(e) => setNewCardOwner(e.target.value)}
      placeholder="Owner (CMO, CTO, CSO, etc.)"
      className="w-full text-sm p-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
     />
     <textarea
      value={newCardDescription}
      onChange={(e) => setNewCardDescription(e.target.value)}
      placeholder="Description"
      rows={3}
      className="w-full text-sm p-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
     />
     <div className="flex items-center gap-2">
      <label className="flex items-center gap-2 text-sm">
       <input
        type="checkbox"
        checked={newCardIsActive}
        onChange={(e) => setNewCardIsActive(e.target.checked)}
        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
       />
       <span className={`font-medium ${newCardIsActive ? 'text-green-700' : 'text-gray-500'}`}>
        {newCardIsActive ? 'Active' : 'Inactive'}
       </span>
      </label>
     </div>
    </div>
    <div className="flex gap-2">
     <button
      onClick={handleSaveNewCard}
      disabled={!newCardName.trim()}
      className="flex-1 text-sm bg-blue-500 text-white px-3 py-2 rounded-md hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
     >
      Save
     </button>
     <button
      onClick={handleCancelAddCard}
      className="flex-1 text-sm bg-gray-500 text-white px-3 py-2 rounded-md hover:bg-gray-600 transition-colors"
     >
      Cancel
     </button>
    </div>
   </div>
  );
 };

 return (
  <div className="flex flex-col lg:flex-row h-full overflow-hidden">
   
   {/* Column 1: Categories (5 C's) */}
   <div className="w-full lg:w-80 border-b lg:border-b-0 lg:border-r border-gray-200 p-3 lg:p-4 overflow-y-auto max-h-40 lg:max-h-full">
    <div className="mb-4">
     <div className="flex justify-between items-start mb-3">
      <h2 className="text-lg font-semibold text-gray-900">Categories</h2>
      {isAdmin && (
       <button
        onClick={() => {
         localStorage.removeItem('cardiovolt-scorecard');
         window.location.reload();
        }}
        className="bg-red-500 hover:bg-red-600 text-white px-3 py-1 text-xs font-semibold rounded-lg transition-colors"
        title="Clear cache and reload"
       >
        Reset
       </button>
      )}
     </div>
     
     {/* Toggle for inactive metrics - moved to separate row */}
     <label className="flex items-center gap-2 text-sm text-gray-600">
      <input
       type="checkbox"
       checked={showInactiveMetrics}
       onChange={(e) => setShowInactiveMetrics(e.target.checked)}
       className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
      />
      Show Inactive
     </label>
    </div>
    <div className="space-y-2">
     {scorecard.currentVersion.map((category) => (
      <button
       key={category.id}
       onClick={() => {
        setSelectedCategory(category.id);
        setSelectedLevel1(null);
        setSelectedLevel2(null);
        setSelectedLevel3(null);
       }}
       className={`w-full text-left p-3 rounded border transition-colors flex items-center justify-between ${
        selectedCategory === category.id
         ? 'bg-blue-50 border-blue-200'
         : 'hover:bg-gray-50 border-gray-200'
       }`}
      >
       <div className="flex-1">
        <div className="font-medium text-base text-gray-900">{category.name}</div>
        <div className="text-xs text-gray-500 mt-1">{category.description}</div>
       </div>
       <div className="text-gray-400 text-sm ml-2">▶</div>
      </button>
     ))}
    </div>
   </div>

   {/* Column 2: Level 1 Metrics (Monthly/Board) */}
   {selectedCategoryData && (
    <div className={`w-full lg:w-80 border-b lg:border-b-0 lg:border-r border-gray-200 p-3 lg:p-4 overflow-y-auto max-h-64 lg:max-h-full lg:block ${selectedLevel1 ? 'hidden' : 'block'}`}>
     <div className="flex items-center justify-between mb-4">
      <h2 className="text-lg font-semibold text-gray-900">Level 1 - Monthly/Board</h2>
      <button 
       onClick={() => {
        setSelectedCategory(null); 
        setSelectedLevel1(null); 
        setSelectedLevel2(null);
        setSelectedLevel3(null);
       }} 
       className="lg:hidden text-gray-400 text-sm px-2 py-1"
      >
       ← Back
      </button>
     </div>
     <div className="space-y-3">
      {selectedCategoryData?.levels[0]?.metrics && filterMetrics(selectedCategoryData.levels[0].metrics).map((metric) => 
       renderMetricCard(metric, 1, selectedCategory!, true)
      )}
      {isAdmin && selectedCategory && renderAddCardForm(1, selectedCategory)}
     </div>
    </div>
   )}

   {/* Column 3: Level 2 Metrics (Weekly) - Always show when Level 1 is selected */}
   {selectedLevel1Data && (
    <div className={`w-full lg:w-80 border-b lg:border-b-0 lg:border-r border-gray-200 p-3 lg:p-4 overflow-y-auto max-h-64 lg:max-h-full lg:block ${selectedLevel2 ? 'hidden' : 'block'}`}>
     <div className="flex items-center justify-between mb-4">
      <h2 className="text-lg font-semibold text-gray-900">Level 2 - Weekly</h2>
      <button 
       onClick={() => {
        setSelectedLevel1(null); 
        setSelectedLevel2(null);
        setSelectedLevel3(null);
       }} 
       className="lg:hidden text-gray-400 text-sm px-2 py-1"
      >
       ← Back
      </button>
     </div>
     <div className="space-y-3">
      {selectedLevel1Data?.subMetrics && filterMetrics(selectedLevel1Data.subMetrics).map((metric) => 
       renderMetricCard(metric, 2, selectedCategory!, true)
      )}
      {isAdmin && selectedLevel1 && renderAddCardForm(2, selectedLevel1)}
     </div>
    </div>
   )}

   {/* Column 4: Level 3 Metrics (Daily) - Show when Level 2 is selected OR when viewing Level 2 sub-metrics */}
   {(selectedLevel2Data || selectedLevel2) && (
    <div className={`w-full lg:w-80 border-b lg:border-b-0 lg:border-r border-gray-200 p-3 lg:p-4 overflow-y-auto max-h-64 lg:max-h-full lg:block ${selectedLevel3 ? 'hidden' : 'block'}`}>
     <div className="flex items-center justify-between mb-4">
      <h2 className="text-lg font-semibold text-gray-900">Level 3 - Daily/Real-time</h2>
      <button 
       onClick={() => {
        setSelectedLevel2(null);
        setSelectedLevel3(null);
       }} 
       className="lg:hidden text-gray-400 text-sm px-2 py-1"
      >
       ← Back
      </button>
     </div>
     <div className="space-y-3">
      {selectedLevel2Data?.subMetrics && filterMetrics(selectedLevel2Data.subMetrics).map((metric) => 
       renderMetricCard(metric, 3, selectedCategory!, true)
      )}
      {isAdmin && selectedLevel2 && renderAddCardForm(3, selectedLevel2)}
     </div>
    </div>
   )}

   {/* Column 5: Level 4 Metrics (Activities) - Show when Level 3 is selected OR when viewing Level 3 sub-metrics */}
   {(selectedLevel3Data || selectedLevel3) && (
    <div className="w-full lg:w-80 p-3 lg:p-4 overflow-y-auto lg:block block">
     <div className="flex items-center justify-between mb-4">
      <h2 className="text-lg font-semibold text-gray-900">Level 4 - Activities</h2>
      <button 
       onClick={() => setSelectedLevel3(null)} 
       className="lg:hidden text-gray-400 text-sm px-2 py-1"
      >
       ← Back
      </button>
     </div>
     <div className="space-y-3">
      {selectedLevel3Data?.subMetrics && filterMetrics(selectedLevel3Data.subMetrics).map((metric) => 
       renderMetricCard(metric, 4, selectedCategory!, false)
      )}
      {isAdmin && selectedLevel3 && renderAddCardForm(4, selectedLevel3)}
     </div>
    </div>
   )}

   {/* Empty state when nothing selected */}
   {!selectedCategory && (
    <div className="flex-1 hidden lg:flex items-center justify-center text-gray-500">
     <div className="text-center">
      <div className="text-lg mb-2"> Select a category to start</div>
      <div className="text-sm">Navigate through the hierarchy: Category → L1 Monthly → L2 Weekly → L3 Daily</div>
     </div>
    </div>
   )}

   {/* Mobile navigation hints */}
   <div className="lg:hidden p-4 bg-gray-50 border-t">
    {!selectedCategory && (
     <div className="text-center text-sm text-gray-600">
       Select a category above to begin exploring metrics
     </div>
    )}
    {selectedCategory && !selectedLevel1 && (
     <div className="text-center text-sm text-gray-600">
       Select a Level 1 metric to see weekly activities
     </div>
    )}
    {selectedLevel1 && !selectedLevel2 && (
     <div className="text-center text-sm text-gray-600">
       Select a Level 2 metric to see daily tracking
     </div>
    )}
    {selectedLevel2 && !selectedLevel3 && (
     <div className="text-center text-sm text-gray-600">
       Select a Level 3 metric to explore individual activities
     </div>
    )}
    {selectedLevel3 && (
     <div className="text-center text-sm text-gray-600">
       Specific activities and trackable team tasks
     </div>
    )}
   </div>

  </div>
 );
}